import { HelpCircle, BarChart3, ArrowRight, TrendingUp, Users, Clock } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

export default function BlackBoxProblem() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.2 });

  return (
    <section ref={sectionRef} className="pt-10 pb-10 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-8 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Your Engineering Black Box
            </h2>
          </div>

          {/* Before/After Visual */}
          <div
            className={`grid md:grid-cols-2 gap-6 mb-6 ${
              isInView ? 'animate-fade-in-up animation-delay-150' : 'opacity-0'
            }`}
          >
            {/* Before */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm font-medium text-red-400 uppercase tracking-wide">Before</span>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-400">
                  <HelpCircle className="w-5 h-5 text-gray-500" />
                  <span>"Why is the team slow? Board wants answers by Monday."</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <HelpCircle className="w-5 h-5 text-gray-500" />
                  <span>"We've grown 3x but velocity hasn't moved. Why?"</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <HelpCircle className="w-5 h-5 text-gray-500" />
                  <span>"Is this a people problem or a process problem?"</span>
                </div>
                <div className="mt-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-500 text-sm italic">
                    "I'm managing by gut feel and it's starting to show."
                  </p>
                </div>
              </div>
            </div>

            {/* After */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-primary-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm font-medium text-green-400 uppercase tracking-wide">After</span>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock className="w-5 h-5 text-primary-400" />
                  <span>"Cycle time increased 40% due to review bottlenecks"</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <TrendingUp className="w-5 h-5 text-primary-400" />
                  <span>"60% of time spent on maintenance vs features"</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Users className="w-5 h-5 text-primary-400" />
                  <span>"3 developers blocked the same reviewer all week"</span>
                </div>
                <div className="mt-4 p-3 bg-primary-900/30 rounded-lg border border-primary-500/30">
                  <p className="text-primary-300 text-sm font-medium">
                    "Here's exactly where we can improve and why."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* DORA Impact Stats */}
          <div
            className={`mb-6 ${
              isInView ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'
            }`}
          >
            <p className="text-center text-sm text-gray-400 uppercase tracking-wide mb-4">
              DORA Research: Why engineering visibility matters
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-400">46x</div>
                <div className="text-sm text-gray-400">More frequent deploys</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-400">4x</div>
                <div className="text-sm text-gray-400">Faster incident recovery</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-400">&frac12;</div>
                <div className="text-sm text-gray-400">Change failure rate</div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-3">
              Source:{' '}
              <a
                href="https://dora.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-gray-400 hover:text-gray-300 transition-colors"
              >
                DORA State of DevOps Report
              </a>
            </p>
          </div>

          {/* Solution Statement */}
          <div
            className={`text-center pt-6 border-t border-gray-800 ${
              isInView ? 'animate-fade-in-up animation-delay-400' : 'opacity-0'
            }`}
          >
            <div className="inline-flex items-center gap-2 text-xl text-primary-400 font-semibold">
              <BarChart3 className="w-6 h-6" />
              <span>CodePulse opens the black box — in minutes, not months.</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
